import { css, html, LitElement, PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { InputWithStatus } from './input-with-status.js';

export type BillingEmailDetail = {
  value: string,
  valid: boolean,
}

// "Looks like an email" — gates the parent's submit/save button on the client.
// The server validates strictly with z.string().email(); this is just to keep
// obviously-bad values from being submitted.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@customElement('pick-billing-email')
export class PickBillingEmail extends LitElement {

  // Pre-filled value. May arrive async (e.g. from a consumed user/org context).
  // Once the user starts typing, `_draft` takes over and `initial` is ignored
  // until the parent calls reset().
  @property({ attribute: false }) initial?: string;
  @property({ type: Boolean }) disabled?: boolean;

  @query('input-with-status') private _input?: InputWithStatus;

  @state() private _draft?: string;
  // Debounce handle for inline validation: surface an error 500ms after the
  // user stops typing, so the disabled submit button isn't the only signal.
  private _validateTimer?: ReturnType<typeof setTimeout>;

  // Trimmed effective value: user-typed draft if any, else `initial`.
  value(): string {
    return (this._draft ?? this.initial ?? '').trim();
  }

  valid(): boolean {
    return EMAIL_RE.test(this.value());
  }

  // Show a one-line inline message under the input. Used by the parent to
  // surface post-save feedback ("Billing email updated" / error message).
  setStatus(success: boolean, message: string) {
    this._input?.setStatus(success, message);
  }

  // Clear any user-typed draft and inline status. Use after a successful save
  // so the input snaps back to displaying the freshly-saved `initial` value
  // instead of the in-flight draft.
  reset() {
    this._draft = undefined;
    this._input?.clearStatus();
  }

  private _onInput() {
    this._draft = this._input?.value() ?? '';
    // Any keystroke supersedes a previous status message ("Saved", error, …).
    this._input?.clearStatus();
    this._dispatch();
    // Re-arm the debounce: only fire validation once the user pauses.
    if (this._validateTimer !== undefined)
      clearTimeout(this._validateTimer);
    this._validateTimer = setTimeout(() => {
      this._validateTimer = undefined;
      this._showInlineValidation();
    }, 500);
  }

  private _dispatch() {
    this.dispatchEvent(new CustomEvent<BillingEmailDetail>('billing-email', {
      detail: { value: this.value(), valid: this.valid() },
    }));
  }

  // Sets the inline status to match the current value's validity. Empty and
  // malformed both surface as red; valid silently clears any prior status.
  private _showInlineValidation() {
    if (!this.value().length)
      this._input?.setStatus(false, 'Billing email must not be empty');
    else if (!this.valid())
      this._input?.setStatus(false, 'Please enter a valid email address');
    else
      this._input?.clearStatus();
  }

  override updated(changed: PropertyValues) {
    // When `initial` settles (from `undefined` to a definite string), broadcast
    // the value and run inline validation so the parent's form state and the
    // inline error message both reflect reality before the user types.
    // - Empty pre-fill → red "must not be empty"
    // - Invalid pre-fill → red "please enter a valid email address"
    // - Valid pre-fill → silent
    // Skip while the user is mid-edit (`_draft` set) so we don't clobber their
    // typed state when the parent re-renders for unrelated reasons.
    if (!changed.has('initial') || this._draft !== undefined || this.initial === undefined)
      return;
    this._dispatch();
    this._showInlineValidation();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this._validateTimer !== undefined) {
      clearTimeout(this._validateTimer);
      this._validateTimer = undefined;
    }
  }

  override render() {
    return html`
      <form-section label="Billing email">
        <div slot=description>Stripe sends invoices and receipts to this address.</div>
        <input-with-status
          type=email
          placeholder="invoices@company.com"
          autocomplete=off
          ?disabled=${this.disabled}
          initial=${this._draft ?? this.initial ?? ''}
          @sl-input=${this._onInput}
        >
          <sl-icon name=envelope slot=prefix></sl-icon>
          <slot></slot>
        </input-with-status>
      </form-section>
    `;
  }

  static styles = [css`
    :host {
      display: block;
    }
  `];
}
